import { apiRequest } from "./api-request";

// Client for the RAG document-management API (U5), proxied by the BFF at /ai/documents* to
// agent-service /agent/documents* (@adr SPARK-0018-bff-proxies-agent-service-as-second-upstream@2). The upload is multipart FormData — apiRequest forwards
// the body untouched, so the browser sets the multipart boundary; every mutating call sends CSRF.

export type DocumentKind = "global" | "uploaded";
export type DocumentStatus = "pending" | "processing" | "ready" | "failed";

export interface AdminDocument {
  id: string;
  kind: DocumentKind;
  title: string;
  description?: string | null;
  sourceName: string;
  status: DocumentStatus;
  disabled: boolean;
  sections: string[];
  errorDetail?: string | null;
  createdAt: string;
  /** Last state change — the loading spinner keys off this (a reprocessed old doc bumps it). */
  updatedAt?: string;
  /** Providers this document has embeddings for — i.e. where its search tool works. */
  embeddedProviders?: string[];
  /** Ingested text-only: figures were skipped (no images). */
  textOnly?: boolean;
}

export class DocumentApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function listDocuments(signal?: AbortSignal): Promise<AdminDocument[]> {
  const { response } = await apiRequest("ai/documents", { headers: { Accept: "application/json" }, signal });
  if (!response.ok) throw new DocumentApiError(response.status, `Failed to list documents (${response.status})`);
  return (await response.json()) as AdminDocument[];
}

export interface UploadResult {
  id: string;
  duplicate: boolean;
}

export async function uploadDocument(input: {
  file: File;
  title: string;
  description?: string;
  sections: string[];
  textOnly?: boolean;
}): Promise<UploadResult> {
  const form = new FormData();
  form.append("file", input.file);
  form.append("title", input.title);
  if (input.description) form.append("description", input.description);
  for (const s of input.sections) form.append("sections", s);
  if (input.textOnly) form.append("textOnly", "true");

  const { response } = await apiRequest("ai/documents", { method: "POST", csrf: true, body: form });
  if (response.status === 409) {
    const body = (await response.json().catch(() => ({}))) as { id?: string };
    return { id: body.id ?? "", duplicate: true };
  }
  if (!response.ok) throw new DocumentApiError(response.status, `Upload failed (${response.status})`);
  const body = (await response.json()) as { id: string };
  return { id: body.id, duplicate: false };
}

/** A chat session's documents (spec §11) — restores the attachment chips on reload. Owner-scoped server-side. */
export async function listSessionDocuments(threadId: string, signal?: AbortSignal): Promise<AdminDocument[]> {
  const { response } = await apiRequest(`ai/documents/session/${encodeURIComponent(threadId)}`, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok) throw new DocumentApiError(response.status, `Failed to list session documents (${response.status})`);
  return (await response.json()) as AdminDocument[];
}

/** One document by id (for status polling of a chat-dropped doc). Reuses the admin detail endpoint. */
export async function getDocument(id: string, signal?: AbortSignal): Promise<AdminDocument> {
  const { response } = await apiRequest(`ai/documents/${encodeURIComponent(id)}`, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok) throw new DocumentApiError(response.status, `Failed to load document (${response.status})`);
  return (await response.json()) as AdminDocument;
}

/**
 * Upload a document scoped to a chat session/thread (spec §6). Coupled to that session + the uploader;
 * visible only within the chat. [textOnly] forces text-only extraction (no figure/image pass). Title
 * defaults server-side to the filename when omitted.
 */
export async function uploadSessionDocument(
  threadId: string,
  input: { file: File; title?: string; textOnly?: boolean },
): Promise<UploadResult> {
  const form = new FormData();
  form.append("file", input.file);
  if (input.title) form.append("title", input.title);
  if (input.textOnly) form.append("textOnly", "true");
  const { response } = await apiRequest(`ai/documents/session/${encodeURIComponent(threadId)}`, {
    method: "POST",
    csrf: true,
    body: form,
  });
  if (!response.ok) throw new DocumentApiError(response.status, `Upload failed (${response.status})`);
  const body = (await response.json()) as { id: string };
  return { id: body.id, duplicate: false };
}

/**
 * Regenerate the chat's follow-up suggestion chips from its attached document(s) + the last message (spec §7).
 * Called when a dropped doc reaches `ready` or is removed. Returns the new chip strings (also persisted
 * server-side). No model turn.
 */
export async function refreshSessionSuggestions(threadId: string): Promise<string[]> {
  const { response } = await apiRequest(`ai/sessions/${encodeURIComponent(threadId)}/suggestions`, {
    method: "POST",
    csrf: true,
  });
  if (!response.ok) throw new DocumentApiError(response.status, `Suggestion refresh failed (${response.status})`);
  return (await response.json()) as string[];
}

/** Remove a chat-dropped document (spec §6); ownership is enforced server-side by session + uploader. */
export async function deleteSessionDocument(threadId: string, id: string): Promise<void> {
  const { response } = await apiRequest(
    `ai/documents/session/${encodeURIComponent(threadId)}/${encodeURIComponent(id)}`,
    { method: "DELETE", csrf: true },
  );
  if (!response.ok) throw new DocumentApiError(response.status, `Delete failed (${response.status})`);
}

export async function patchDocument(
  id: string,
  patch: { title?: string; description?: string; sections?: string[]; disabled?: boolean },
): Promise<void> {
  const { response } = await apiRequest(`ai/documents/${id}`, {
    method: "PATCH",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw new DocumentApiError(response.status, `Update failed (${response.status})`);
}

export async function deleteDocument(id: string): Promise<void> {
  const { response } = await apiRequest(`ai/documents/${id}`, { method: "DELETE", csrf: true });
  if (!response.ok) throw new DocumentApiError(response.status, `Delete failed (${response.status})`);
}

/** Cancel an in-flight reprocess: reverts the document to its prior version, discarding the new run. */
export async function cancelReprocess(id: string): Promise<void> {
  const { response } = await apiRequest(`ai/documents/${id}/cancel`, { method: "POST", csrf: true });
  if (!response.ok) throw new DocumentApiError(response.status, `Cancel failed (${response.status})`);
}

export async function reprocessDocument(id: string, provider?: string): Promise<void> {
  const q = provider ? `?provider=${encodeURIComponent(provider)}` : "";
  const { response } = await apiRequest(`ai/documents/${id}/reprocess${q}`, { method: "POST", csrf: true });
  if (!response.ok) throw new DocumentApiError(response.status, `Reprocess failed (${response.status})`);
}

/** Fetch an uploaded document's stored original and trigger a browser download. */
export async function downloadOriginal(id: string, filename: string): Promise<void> {
  const { response } = await apiRequest(`ai/documents/${id}/original`, {
    headers: { Accept: "application/pdf" },
  });
  if (!response.ok) throw new DocumentApiError(response.status, `Download failed (${response.status})`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
